// Student landing page (ADO #314).
//
// Layout:
//   • Sticky top header — F2C logo · Dashboard · Browse course · 🔔 · 👤
//   • Welcome hero (2/3 width) + 4 compact stat tiles (1/3, 2x2)
//   • Next-up LO card
//   • Top Picks for You — enrolled + available courses, all in one grid
//
// No left sidebar, no charts, no calendar, no activity feed (we don't have
// the data; honest defer). Sparklines on stat tiles are intentionally
// omitted — fake data on a "Completed" tile would erode trust.

import React, { useEffect, useMemo, useState } from 'react';
import { track } from '@/services/learnerEvents';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { BrandLockup } from '@/components/BrandLockup';
import { UserMenu } from '@/components/UserMenu';
import { CourseCoverArt, hasCustomCover } from '@/components/CourseCovers';
import CollegePrompt from '@/components/CollegePrompt';
import WhatsAppPrompt from '@/components/WhatsAppPrompt';
import { getWhatsApp } from '@/services/whatsappService';
import { getStartedCapstones } from '@/services/capstoneService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  PlayCircle,
  Target,
  ArrowRight,
  BookOpen,
  Flame,
  CheckCircle2,
  Clock,
  GraduationCap,
  Lock,
  Sparkles,
  Bell,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const COURSE_SLUG = 'c-programming';

// ---------- helpers ----------

const stripHtml = (html) => {
  if (!html) return '';
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
  }
  return String(html).replace(/<[^>]+>/g, '').trim();
};

const titleise = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replaceAll('_', ' ') : '';

const displayNameFromUser = (user) =>
  user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'there';

const emailFromUser = (user) => user?.email || '';

const firstInitial = (user) =>
  (displayNameFromUser(user).charAt(0) || 'S').toUpperCase();

const timeOfDayGreeting = () => {
  const h = new Date().getHours();
  if (h < 5)  return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Burning the midnight oil';
};


// ---------- top header ----------

const NotificationsBell = () => (
  <DropdownMenu>
    <DropdownMenuTrigger
      className="relative w-10 h-10 rounded-full hover:bg-slate-50 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
      aria-label="Notifications"
      data-testid="notifications-trigger"
    >
      <Bell className="h-5 w-5 text-slate-600" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-72">
      <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
        Notifications
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <div className="px-3 py-6 text-center">
        <Sparkles className="h-6 w-6 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-600">You're all caught up</p>
        <p className="text-xs text-slate-500 mt-1">Activity alerts will appear here.</p>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
);

// UserDropdown was extracted to the shared <UserMenu /> so the Learn and
// Solution page headers render the same working menu (self-contained: reads
// user + signOut from AuthContext).

const TopHeader = () => (
  <header
    className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100"
    data-testid="dashboard-header"
  >
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
      <BrandLockup variant="light" data-testid="brand-logo" />

      <div className="flex items-center gap-2 flex-shrink-0">
        <NotificationsBell />
        <UserMenu />
      </div>
    </div>
  </header>
);


// ---------- welcome hero ----------

// Inline SVG illustration — stacked books + graduation cap + decorative
// shapes. Drawn here so we don't ship a binary asset for one card.
const WelcomeIllustration = ({ className = '' }) => (
  <svg
    viewBox="0 0 360 260"
    className={className}
    role="img"
    aria-label="A stack of books with a graduation cap and floating sparkles"
  >
    {/* Soft background blob */}
    <ellipse cx="180" cy="150" rx="160" ry="95" fill="#bfdbfe" opacity="0.55" />

    {/* Floating shapes — orange / emerald / blue / purple accents */}
    <circle cx="40"  cy="40"  r="4"  fill="#f97316" />
    <circle cx="320" cy="40"  r="5"  fill="#10b981" />
    <circle cx="30"  cy="200" r="3"  fill="#8b5cf6" />
    <circle cx="335" cy="220" r="4"  fill="#f59e0b" />
    {/* Tiny diamonds */}
    <path d="M70 80 l4 4 -4 4 -4 -4z"   fill="#3b82f6" />
    <path d="M290 90 l4 4 -4 4 -4 -4z"  fill="#f97316" />
    <path d="M50 140 l3 3 -3 3 -3 -3z"  fill="#10b981" />
    {/* Sparkle 4-point stars */}
    <path d="M310 170 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3z" fill="#f59e0b" opacity="0.8" />
    <path d="M85 200 l2 6 6 2 -6 2 -2 6 -2 -6 -6 -2 6 -2z"   fill="#3b82f6" opacity="0.8" />

    {/* Pot + plant (left bottom) */}
    <path d="M85 165 q15 -22 30 0" stroke="#16a34a" strokeWidth="2.5" fill="none" />
    <ellipse cx="100" cy="158" rx="6"  ry="11" fill="#22c55e" />
    <ellipse cx="93"  cy="170" rx="5"  ry="9"  fill="#16a34a" />
    <ellipse cx="108" cy="170" rx="5"  ry="9"  fill="#16a34a" />
    <path d="M83 188 l5 22 l24 0 l5 -22z" fill="#fb923c" />

    {/* Stack of books (center) */}
    {/* Book 1 — blue (bottom) */}
    <rect x="125" y="208" width="170" height="22" rx="4" fill="#3b82f6" />
    <rect x="135" y="215" width="8"   height="8"  rx="1" fill="#1e3a8a" opacity="0.5" />
    {/* Book 2 — orange */}
    <rect x="135" y="186" width="160" height="22" rx="4" fill="#f97316" />
    <rect x="145" y="193" width="8"   height="8"  rx="1" fill="#9a3412" opacity="0.5" />
    {/* Book 3 — emerald */}
    <rect x="145" y="164" width="150" height="22" rx="4" fill="#10b981" />
    <rect x="155" y="171" width="8"   height="8"  rx="1" fill="#064e3b" opacity="0.5" />
    {/* Book 4 — purple */}
    <rect x="155" y="142" width="140" height="22" rx="4" fill="#8b5cf6" />
    <rect x="165" y="149" width="8"   height="8"  rx="1" fill="#4c1d95" opacity="0.5" />
    {/* Top open book — white pages */}
    <path d="M165 122 l60 -14 l60 14 l-60 14z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
    <line x1="225" y1="108" x2="225" y2="136" stroke="#cbd5e1" strokeWidth="1" />

    {/* Graduation cap (top of stack) */}
    <path d="M195 92 l30 -14 l30 14 l-30 14z" fill="#0f172a" />
    <rect x="252" y="86"  width="3" height="22" fill="#0f172a" />
    <path d="M255 108 q8 4 0 14" stroke="#0f172a" strokeWidth="2" fill="none" />
    <circle cx="255" cy="124" r="4" fill="#f97316" />
    {/* Cap tassel highlight */}
    <path d="M210 96 l15 -7 l15 7" stroke="#1e293b" strokeWidth="1" fill="none" opacity="0.5" />

    {/* Laptop in foreground (small) */}
    <rect x="60"  y="200" width="40" height="22" rx="2"  fill="#475569" />
    <rect x="63"  y="203" width="34" height="14" rx="1"  fill="#cbd5e1" />
    <path d="M55 222 l50 0 l-5 4 l-40 0z" fill="#334155" />
  </svg>
);

const WelcomeHero = ({ greeting, name, course, continueHref }) => (
  <div
    className="relative overflow-hidden rounded-2xl bg-blue-50 border border-blue-100 shadow-sm h-full"
    data-testid="welcome-hero"
  >
    <div className="flex items-stretch h-full">
      {/* Left: content */}
      <div className="flex-1 min-w-0 px-6 py-7 sm:px-8 sm:py-8 flex flex-col justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2">
            {greeting}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight text-slate-900">
            Hi, {name?.split(' ')[0] || 'there'} <span className="inline-block">👋</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-md leading-relaxed">
            {course
              ? `Pick up where you left off in ${course.title}, or explore something new.`
              : 'Discover courses, track progress, and master programming step by step.'}
          </p>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            asChild
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
          >
            <Link to={continueHref} data-testid="continue-learning-btn">
              <PlayCircle className="h-5 w-5 mr-2" />
              {course ? 'Continue learning' : 'Explore courses'}
            </Link>
          </Button>
        </div>
      </div>

      {/* Right: illustration (hidden on mobile so the card stays compact) */}
      <div className="hidden sm:flex items-end w-[44%] max-w-[360px] flex-shrink-0">
        <WelcomeIllustration className="w-full h-auto" />
      </div>
    </div>
  </div>
);


// ---------- stat tiles (2x2 alongside hero) ----------

const StatTile = ({ icon: Icon, value, label, accent, accentText }) => (
  <Card className="border-slate-200 shadow-sm h-full">
    <CardContent className="p-3.5 sm:p-4 h-full flex flex-col justify-between">
      <div className={`w-9 h-9 rounded-lg ${accent} ${accentText} flex items-center justify-center`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="mt-2.5">
        <p className="text-2xl sm:text-3xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1.5">{label}</p>
      </div>
    </CardContent>
  </Card>
);


// ---------- next-up card ----------

const NextUpCard = ({ nba, continueHref }) => {
  if (!nba?.lo) return null;
  const lo = nba.lo;
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="next-up-card">
      <CardContent className="py-5 sm:py-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Target className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Next up</p>
            <p className="text-base sm:text-lg text-slate-900 font-semibold leading-snug">
              {lo.statement}
            </p>
            {nba.topic && (
              <p className="text-xs text-slate-500 mt-1.5">
                Topic: <span className="text-slate-700 font-medium">{nba.topic.title}</span>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <Pill colour="bg-blue-50 text-blue-700 border-blue-200">{titleise(lo.blooms_level)}</Pill>
              <Pill colour="bg-emerald-50 text-emerald-700 border-emerald-200">{titleise(lo.knowledge_dimension)}</Pill>
              {lo.thinking_tier && (
                <Pill colour="bg-slate-100 text-slate-700 border-slate-200">{lo.thinking_tier}</Pill>
              )}
            </div>
          </div>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0">
            <Link to={continueHref} data-testid="next-up-start-btn">
              Start
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ---------- shared ----------

const Pill = ({ children, colour }) => (
  <span className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium border ${colour}`}>
    {children}
  </span>
);

const StatusBadge = ({ status }) => {
  const map = {
    completed:    { label: 'Completed',   colour: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    in_progress:  { label: 'In progress', colour: 'bg-blue-50 text-blue-700 border-blue-200',          icon: Clock },
    not_started:  { label: 'Not started', colour: 'bg-slate-50 text-slate-600 border-slate-200',       icon: PlayCircle },
    coming_soon:  { label: 'Coming soon', colour: 'bg-slate-50 text-slate-500 border-slate-200',       icon: Lock },
    available:    { label: 'Available',   colour: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Sparkles },
  };
  const cfg = map[status] || map.not_started;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border ${cfg.colour}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const ProgressBar = ({ pct }) => {
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 transition-[width] duration-300"
          style={{ width: `${safe}%` }}
        />
      </div>
    </div>
  );
};

const CourseCover = ({ course }) => {
  // Track whether the image actually loaded — fall back if it errors (e.g.
  // current production data points at a Salesforce sandbox URL that requires
  // auth and 401s from the browser).
  const [broken, setBroken] = useState(false);
  const initial = (course.title || '?').charAt(0).toUpperCase();

  if (course.image_url && !broken) {
    return (
      <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
        <img
          src={course.image_url}
          alt={course.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      </div>
    );
  }
  // ADO follow-up to #331 — inline SVG cover for known courses (Problem
  // Solving + the four per-language tracks) so the dashboard doesn't fall
  // back to a bare letter tile while authored cover art doesn't exist yet.
  if (hasCustomCover(course.slug)) {
    return (
      <div className="aspect-[16/9] overflow-hidden">
        <CourseCoverArt slug={course.slug} />
      </div>
    );
  }
  // Fallback: pastel block with course initial.
  return (
    <div className="aspect-[16/9] bg-blue-50 flex items-center justify-center">
      <span className="text-6xl font-bold text-blue-300 select-none">{initial}</span>
    </div>
  );
};


// ---------- course card (one shape for both enrolled and available) ----------

const CoursePickCard = ({ course }) => {
  const isComingSoon = (course.status_label || '').toLowerCase() === 'coming soon';
  const isEnrolled   = Boolean(course.progress); // shape from enrolled_courses
  const isPaid       = (course.price_inr ?? 0) > 0; // ADO #481 — paid vs free
  const pct          = course.progress?.pct ?? 0;
  const nbaLoId      = course.next_best_action?.lo?.id;
  // Always carry the course slug so /learn shows THIS course's tree, not the
  // Sidebar's C default.
  const courseQ      = `course=${encodeURIComponent(course.slug)}`;
  const continueHref = nbaLoId ? `/learn?${courseQ}&lo=${encodeURIComponent(nbaLoId)}` : `/learn?${courseQ}`;
  // ADO #481 — a not-enrolled PAID course must go to the purchase flow, never
  // straight into /learn (the paid content is gated server-side too).
  const availableHref = isPaid ? `/cart?add=${encodeURIComponent(course.slug)}` : `/learn?${courseQ}`;

  let badgeStatus;
  if (isEnrolled)         badgeStatus = course.status;       // in_progress | completed | not_started
  else if (isComingSoon)  badgeStatus = 'coming_soon';
  else                    badgeStatus = 'available';

  const ctaLabel =
    isComingSoon ? 'Notify me' :
    !isEnrolled  ? (isPaid ? 'Enroll now' : 'Start course') :
    pct >= 100   ? 'Review' :
    pct > 0      ? 'Resume' :
                   'Continue';

  return (
    <Card
      className={`border-slate-200 shadow-sm overflow-hidden transition-[transform,box-shadow] duration-200 flex flex-col ${
        isComingSoon ? '' : 'hover:shadow-md hover:-translate-y-0.5'
      }`}
      data-testid={`course-pick-${course.slug}`}
    >
      <CourseCover course={course} />
      <CardContent className="py-4 sm:py-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-base font-semibold text-slate-900 leading-tight line-clamp-2">
            {course.title}
          </h3>
          <StatusBadge status={badgeStatus} />
        </div>
        {(course.level || course.duration_weeks) && (
          <p className="text-xs text-slate-500 mb-2">
            {course.level}
            {course.level && course.duration_weeks ? ' · ' : ''}
            {course.duration_weeks ? `${course.duration_weeks} weeks` : ''}
          </p>
        )}

        {course.instructor_name && (
          <div className="flex items-center gap-2 mb-3 mt-auto">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="bg-blue-100 text-blue-700 text-[10px] font-semibold">
                {(course.instructor_name || '?').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <p className="text-xs text-slate-500">
              by <span className="text-slate-700 font-medium">{course.instructor_name}</span>
            </p>
          </div>
        )}

        {isEnrolled && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
              <span>Progress</span>
              <span className="font-medium text-slate-700">{pct}%</span>
            </div>
            <ProgressBar pct={pct} />
            <p className="text-[10px] text-slate-500 mt-1">
              {course.progress.los_completed} / {course.progress.los_total} LOs
            </p>
          </div>
        )}

        <div className="mt-auto">
          {isComingSoon ? (
            // #570: was a disabled button, so interest in a coming course was
            // never seen. The click is the signal; nothing else is promised.
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => {
                track('notify.clicked', course.slug);
                toast.success(`Thanks, we have noted your interest in ${course.title}.`);
              }}
            >
              {ctaLabel}
            </Button>
          ) : isEnrolled ? (
            <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full">
              <Link to={continueHref}>
                {ctaLabel}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          ) : (
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link
                to={availableHref}
                onClick={isPaid ? () => track('upgrade.clicked', course.slug, { where: 'dashboard' }) : undefined}
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


// ---------- main page ----------

// The count is the board's own rule (latest attempt passed), so the card and the
// board can never disagree.
const CapstoneCard = ({ capstone: c }) => (
  <Link
    to={`/capstone/${encodeURIComponent(c.slug)}`}
    data-testid={`dashboard-capstone-${c.slug}`}
    className="block rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-emerald-300 hover:shadow transition"
  >
    <div className="flex items-center gap-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
        <Target className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Capstone</p>
        <p className="text-sm sm:text-base font-semibold text-slate-900 truncate">{c.title}</p>
      </div>
      <p className="text-sm text-slate-600 whitespace-nowrap">
        <span className="font-semibold text-slate-900">{c.done}</span> of {c.total} stories done
      </p>
      <ArrowRight className="h-4 w-4 text-slate-400" />
    </div>
  </Link>
);

export const DashboardPage = () => {
  useEffect(() => { track('page.viewed', 'dashboard'); }, []);   // #570
  const { user, session } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collegeDismissed, setCollegeDismissed] = useState(false);
  // ADO #572 — null until known; stays null when the server can't say (e.g.
  // migration 447 not applied), so the dashboard simply doesn't ask.
  const [whatsapp, setWhatsapp] = useState(null);
  const [whatsappDismissed, setWhatsappDismissed] = useState(false);

  useEffect(() => {
    if (!session?.access_token) return undefined;
    let cancelled = false;
    getWhatsApp()
      .then((d) => { if (!cancelled) setWhatsapp(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session]);
  // ADO #510 — capstones the student has started (a project exists). Fetched
  // apart from the summary: a capstone failure must not blank the dashboard.
  const [capstones, setCapstones] = useState([]);

  useEffect(() => {
    if (!session?.access_token) return undefined;
    let cancelled = false;
    getStartedCapstones()
      .then((d) => { if (!cancelled) setCapstones(d.capstones || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session]);

  useEffect(() => {
    // ADO #480 — student-summary is now auth-required and user-scoped, so the
    // request must carry the access token. Wait until the session is available.
    const token = session?.access_token;
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_URL}/api/learn/student-summary?course_slug=${encodeURIComponent(COURSE_SLUG)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`student-summary failed: ${res.status}`);
        return res.json();
      })
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load dashboard'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [session]);


  // Tolerant adapter — works against both v1 and multi-course backend.
  const view = useMemo(() => {
    if (!summary) return null;
    // ADO #482 — trust the user-scoped enrolled_courses from the backend. Do NOT
    // fall back to the focus course (summary.course defaults to c-programming):
    // that fabricated a paid-course "enrollment" for users with no enrollments.
    const enrolledCourses = Array.isArray(summary.enrolled_courses) ? summary.enrolled_courses : [];
    const isComingSoon = (c) => (c.status_label || '').toLowerCase() === 'coming soon';
    // Show all "Coming Soon" courses last, preserving the backend's order_index
    // order within each group (Array.sort is stable in modern engines).
    const availableCourses = (Array.isArray(summary.available_courses) ? summary.available_courses : [])
      .slice()
      .sort((a, b) => (isComingSoon(a) ? 1 : 0) - (isComingSoon(b) ? 1 : 0));
    const stats = summary.stats || {
      enrolled:    enrolledCourses.length,
      in_progress: enrolledCourses.filter((c) => c.status === 'in_progress').length,
      completed:   enrolledCourses.filter((c) => c.status === 'completed').length,
      los_total:   enrolledCourses.reduce((sum, c) => sum + (c.progress?.los_total || 0), 0),
    };
    return { enrolledCourses, availableCourses, stats };
  }, [summary]);

  // Pick the focus course (first in_progress, else first not_started, else first enrolled).
  const focusCourse = useMemo(() => {
    const enrolled = view?.enrolledCourses || [];
    if (!enrolled.length) return null;
    return enrolled.find((c) => c.status === 'in_progress')
        || enrolled.find((c) => c.status === 'not_started')
        || enrolled[0];
  }, [view]);

  const studentName  = displayNameFromUser(user);
  const greeting     = useMemo(() => timeOfDayGreeting(), []);
  const nbaLoId      = focusCourse?.next_best_action?.lo?.id;
  // Carry the focus course slug so "Continue learning" opens the right course's
  // tree (was defaulting to C via the Sidebar).
  const focusQ       = focusCourse ? `course=${encodeURIComponent(focusCourse.slug)}` : '';
  const continueHref = focusCourse
    ? (nbaLoId ? `/learn?${focusQ}&lo=${encodeURIComponent(nbaLoId)}` : `/learn?${focusQ}`)
    : '/learn';

  // Enrolled and available stay in their own sections — enrolled courses
  // must NOT appear in recommendations.
  const myCourses          = view?.enrolledCourses  || [];
  const recommendedCourses = view?.availableCourses || [];

  // Phase A — prompt for college once, when we still don't know it and the
  // student hasn't already submitted an unlisted suggestion (source 'pending').
  const collegeAsked =
    !!summary && !summary.college && summary.college_source !== 'pending';
  const showCollegePrompt = collegeAsked && !collegeDismissed;

  // ADO #572 — ask for WhatsApp every visit until a number is saved, but one
  // ask per visit: on a visit that asks for the college (even once it is
  // skipped or answered), WhatsApp waits for the next one. A learner who turned
  // messages off on their profile (opt_out_at) is not asked again.
  const showWhatsAppPrompt =
    !!summary && !collegeAsked && whatsapp !== null
    && !whatsapp.opt_in && !whatsapp.opt_out_at && !whatsappDismissed;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {showCollegePrompt && <CollegePrompt onDone={() => setCollegeDismissed(true)} />}
      {showWhatsAppPrompt && <WhatsAppPrompt onDone={() => setWhatsappDismissed(true)} />}
      <TopHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

        {loading && (
          <div className="text-sm text-slate-500" data-testid="dashboard-loading">
            Loading your dashboard…
          </div>
        )}

        {error && !loading && (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4"
            data-testid="dashboard-error"
          >
            Failed to load dashboard: {error}
          </div>
        )}

        {!loading && !error && view && (
          <div className="space-y-6 sm:space-y-8">

            {/* Hero (2/3) + 2x2 stats (1/3) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
              <div className="lg:col-span-2">
                <WelcomeHero
                  greeting={greeting}
                  name={studentName}
                  course={focusCourse}
                  continueHref={continueHref}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="stats-row">
                <StatTile
                  icon={BookOpen}
                  value={view.stats.enrolled}
                  label="Enrolled"
                  accent="bg-blue-50"
                  accentText="text-blue-600"
                />
                <StatTile
                  icon={Clock}
                  value={view.stats.in_progress}
                  label="In progress"
                  accent="bg-orange-50"
                  accentText="text-orange-500"
                />
                <StatTile
                  icon={CheckCircle2}
                  value={view.stats.completed}
                  label="Completed"
                  accent="bg-emerald-50"
                  accentText="text-emerald-600"
                />
                <StatTile
                  icon={GraduationCap}
                  value={view.stats.los_total}
                  label="LOs ready"
                  accent="bg-slate-100"
                  accentText="text-slate-700"
                />
              </div>
            </div>

            {/* Next up */}
            <NextUpCard
              nba={focusCourse?.next_best_action}
              continueHref={continueHref}
            />

            {/* ADO #510 — one card per started capstone, straight to its board. */}
            {capstones.map((c) => (
              <CapstoneCard key={c.slug} capstone={c} />
            ))}

            {/* My Courses — enrolled only */}
            {myCourses.length > 0 && (
              <section data-testid="my-courses-section">
                <div className="flex items-end justify-between mb-3 sm:mb-4">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">
                      My courses
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Continue right where you left off.
                    </p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {myCourses.length}
                    {myCourses.length === 1 ? ' course' : ' courses'} enrolled
                  </p>
                </div>
                <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {myCourses.map((c) => (
                    <CoursePickCard key={c.id} course={c} />
                  ))}
                </div>
              </section>
            )}

            {/* Top Picks for You — recommendations only (NOT enrolled) */}
            {recommendedCourses.length > 0 && (
              <section data-testid="top-picks-section">
                <div className="flex items-end justify-between mb-3 sm:mb-4">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">
                      Top Picks for You
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      What's coming next in the catalog.
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {recommendedCourses.map((c) => (
                    <CoursePickCard key={c.id} course={c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
